import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { ConfirmModal } from "../../../components/ConfirmModal";
import { useAuth } from "../../../components/contexts/AuthContext";
import {
  createConnectLink,
  disconnectSocialAccount,
  getClientSocial,
  publishingErrorText,
  syncClientSocial,
} from "../../../services/socialPublishingService";
import { platformLabel } from "../../ContentGenerator/workflow/platformMeta";
import { ConnectWarning } from "./ConnectWarning";
import {
  consumePortalOpened,
  markPortalOpened,
  readPortalReturn,
} from "./portalReturn";

export { readPortalReturn };

/**
 * bundle.social connections of a client: accounts by platform, the connect flow
 * (with the connect warning) and the handling of the return from the portal.
 * Render `dialogs` somewhere in the component that uses it.
 */
export function useSocialConnections(clientId, { enabled = true, onSuccess, onError } = {}) {
  const { interceptorReady } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy] = useState(null); // platform key or "sync"
  const [confirmFor, setConfirmFor] = useState(null); // platform awaiting the warning
  const [disconnectFor, setDisconnectFor] = useState(null); // platform awaiting confirmation
  const handledReturnRef = useRef(false);

  const active = enabled && Boolean(clientId);
  const queryKey = ["client-social", clientId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getClientSocial(clientId),
    enabled: interceptorReady && active,
  });

  const accounts = useMemo(() => {
    const map = {};
    (data?.accounts || []).forEach((account) => {
      map[account.platform] = account;
    });
    return map;
  }, [data]);

  // Back from the portal: show what happened, resync and clean the URL.
  useEffect(() => {
    if (!interceptorReady || !active || handledReturnRef.current) return;
    const results = readPortalReturn(searchParams);
    const returned = consumePortalOpened(clientId);
    if (!results.length && !returned) return;
    handledReturnRef.current = true;
    if (results.length) {
      const next = new URLSearchParams(searchParams);
      results.forEach((result) => next.delete(result.key));
      setSearchParams(next, { replace: true });
    }
    const failed = results.find((result) => result.severity === "error");
    const before = queryClient.getQueryData(queryKey);
    const wasConnected = new Set(
      (before?.accounts || []).filter((account) => account.status === "connected").map((account) => account.platform)
    );
    setBusy("sync");
    syncClientSocial(clientId)
      .then((updated) => {
        queryClient.setQueryData(queryKey, updated);
        if (failed) {
          onError?.(failed.message);
          return;
        }
        const connected = (updated.accounts || []).filter((account) => account.status === "connected");
        const added = before ? connected.filter((account) => !wasConnected.has(account.platform)) : [];
        onSuccess?.(
          added.length
            ? `${added.map((account) => platformLabel(account.platform)).join(", ")} ${added.length === 1 ? "conectada" : "conectadas"}`
            : `Conexión comprobada: ${connected.length} ${connected.length === 1 ? "red conectada" : "redes conectadas"}`
        );
      })
      .catch((err) => {
        onError?.(failed?.message || publishingErrorText(err, "No se han podido comprobar las conexiones."));
      })
      .finally(() => setBusy(null));
  }, [interceptorReady, active, clientId, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPortal = async (busyKey) => {
    setBusy(busyKey);
    try {
      const url = await createConnectLink(clientId);
      markPortalOpened(clientId);
      window.location.assign(url);
    } catch (err) {
      onError?.(publishingErrorText(err, "No se ha podido abrir la conexión."));
      setBusy(null);
    }
  };

  const connect = (platform) => setConfirmFor(platform);

  const confirmDisconnect = async () => {
    const platform = disconnectFor;
    setDisconnectFor(null);
    setBusy(platform);
    try {
      const updated = await disconnectSocialAccount(clientId, platform);
      queryClient.setQueryData(queryKey, updated);
      onSuccess?.(`${platformLabel(platform)} desconectada`);
    } catch (err) {
      onError?.(publishingErrorText(err, "No se ha podido desconectar la red."));
    } finally {
      setBusy(null);
    }
  };

  const dialogs = (
    <>
      <ConnectWarning
        open={Boolean(confirmFor)}
        platforms={confirmFor ? [confirmFor] : []}
        onCancel={() => setConfirmFor(null)}
        onConfirm={() => {
          const platform = confirmFor;
          setConfirmFor(null);
          openPortal(platform);
        }}
      />
      <ConfirmModal
        open={Boolean(disconnectFor)}
        onClose={() => setDisconnectFor(null)}
        onConfirm={confirmDisconnect}
        title={disconnectFor ? `Desconectar ${platformLabel(disconnectFor)}` : "Desconectar"}
        description="Los contenidos programados en esta red no se publicarán. ¿Seguro que quieres desconectarla?"
        confirmLabel="Desconectar"
        tone="warning"
      />
    </>
  );

  return {
    accounts,
    isLoading,
    busy,
    connect,
    disconnect: setDisconnectFor,
    dialogs,
  };
}
